import React, { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChartLineUpIcon, CurrencyDollarSimpleIcon } from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const CATEGORIES = [
  "Sneakers",
  "Fragrances & Perfume",
  "Luxury Bags & Accessories",
  "Storage Unit Finds",
  "Trading Card Games",
  "Electronics",
  "Funko",
  "Rocks & Crystals",
  "Sports Cards",
  "Plants",
  "Women's Apparel",
  "Jewelry",
];

export default function Prices() {
  const [overview, setOverview] = useState([]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [trend, setTrend] = useState(null);
  const [loadingTrend, setLoadingTrend] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/prices/overview");
        setOverview(r.data.categories || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingTrend(true);
      try {
        const r = await api.get(`/prices/${encodeURIComponent(category)}`);
        setTrend(r.data);
      } catch {
        setTrend(null);
      }
      setLoadingTrend(false);
    })();
  }, [category]);

  const chartData = useMemo(
    () => (trend?.weeks || []).map((w) => ({ ...w, name: w.label })),
    [trend]
  );

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="prices-page">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="overline text-zinc-500 flex items-center gap-2">
              <ChartLineUpIcon size={14} /> MARKET PRICE TRACKER
            </p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
              Know what it's worth
              <br />
              before you bid.
            </h1>
            <p className="mt-3 text-zinc-600 max-w-xl">
              Actual sold prices from Bidorah, aggregated weekly. Your cheat sheet for every drop.
            </p>
          </div>
        </div>

        <section className="mt-10" data-testid="prices-overview">
          <h2 className="overline text-zinc-500">LAST 30 DAYS · BY CATEGORY</h2>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CATEGORIES.map((c) => {
              const row = overview.find((o) => o.category === c);
              const selected = c === category;
              return (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`text-left rounded-xl border p-4 transition ${
                    selected
                      ? "border-[#1D78AB] bg-[#1D78AB]/5 shadow-sm"
                      : "border-zinc-200 hover:border-[#1D78AB]/50"
                  }`}
                  data-testid={`price-cat-${c.replace(/[^a-z0-9]+/gi, "-")}`}
                >
                  <p className="text-xs font-bold text-zinc-500 truncate">{c}</p>
                  <p className="mt-1 font-display font-black text-2xl tracking-tight">
                    {row ? `$${row.avg.toFixed(0)}` : "—"}
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    {row ? `${row.count} sold` : "no data yet"}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-12" data-testid="prices-trend">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-display font-black text-2xl tracking-tighter">
                {category} — 12-week trend
              </h2>
              <p className="text-sm text-zinc-600">
                Weekly average of paid sales. Hover the chart for detail.
              </p>
            </div>
            {trend?.summary?.count ? (
              <div className="flex gap-3 text-right text-sm">
                <Stat label="Avg" value={`$${trend.summary.avg.toFixed(0)}`} />
                <Stat label="Low" value={`$${trend.summary.low.toFixed(0)}`} />
                <Stat label="High" value={`$${trend.summary.high.toFixed(0)}`} />
                <Stat label="Sold" value={trend.summary.count} />
              </div>
            ) : null}
          </div>

          <div className="mt-5 h-72 rounded-2xl border border-zinc-200 p-4 bg-white">
            {loadingTrend ? (
              <div className="h-full grid place-items-center text-zinc-400 text-sm">
                Loading…
              </div>
            ) : chartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1D78AB" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#1D78AB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" fontSize={11} stroke="#888" />
                  <YAxis fontSize={11} stroke="#888" />
                  <Tooltip
                    formatter={(v) => `$${Number(v).toFixed(2)}`}
                    contentStyle={{ borderRadius: 12, borderColor: "#ddd" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="avg"
                    stroke="#1D78AB"
                    strokeWidth={2}
                    fill="url(#priceGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-center text-zinc-500 text-sm">
                <div>
                  <CurrencyDollarSimpleIcon size={32} className="mx-auto text-zinc-300" />
                  <p className="mt-2">No sales data for this category yet.</p>
                  <p className="text-xs mt-1">Once a few sales land, the chart will come alive.</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {trend?.recent_sold?.length > 0 && (
          <section className="mt-10" data-testid="prices-recent">
            <h2 className="overline text-zinc-500">RECENTLY SOLD IN {category.toUpperCase()}</h2>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {trend.recent_sold.map((r, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-zinc-200 overflow-hidden hover:shadow-md transition"
                >
                  <div className="aspect-square bg-zinc-100 overflow-hidden">
                    {r.image ? (
                      <img src={r.image} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-zinc-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                    </p>
                    <p className="font-semibold text-sm line-clamp-2">{r.title}</p>
                    <p className="text-[#1D78AB] font-black mt-1">
                      ${Number(r.amount || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-16 rounded-2xl border border-zinc-200 p-6 bg-zinc-50 text-center">
          <p className="font-display font-black text-2xl tracking-tighter">
            Ready to put it to work?
          </p>
          <p className="text-sm text-zinc-600 mt-1">
            Find live drops priced below the market.
          </p>
          <Link
            to="/browse"
            className="mt-4 inline-block bg-[#1D78AB] text-white px-6 py-3 rounded-full font-bold hover:bg-[#0F3C57]"
            data-testid="prices-browse-link"
          >
            Browse listings
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2">
      <p className="text-[10px] font-bold tracking-widest text-zinc-500">{label}</p>
      <p className="font-display font-black text-lg">{value}</p>
    </div>
  );
}
